<template>
  <wui-modal
    :size="40"
    :closable="true"
    class="flex h-160! max-h-[calc(100vh-32px)]! w-[min(32.5rem,calc(100vw-32px))]! flex-col overflow-hidden! pb-0! [--modal-gap:0px] [--modal-pad:16px] [--modal-radius:16px] [--modal-title-pad-y:0px] [&_.wui-modal__content]:min-h-0 [&_.wui-modal__content]:flex-1 [&_.wui-modal__content]:overflow-hidden"
    @close="closeDialog"
  >
    <template #header>
      <h2 class="text-heading-sb-18-24 text-fg-base-fg3">
        {{ modalTitle }}
      </h2>
    </template>

    <div class="flex h-full min-h-0 flex-col">
      <div class="shrink-0 pt-1 pb-2">
        <wui-input
          v-model="search"
          :placeholder="$t('common.search')"
          with-clear
          prepend-icon="searchM"
          data-test="share-contact-search"
        />
      </div>

      <div
        v-if="folders.length"
        class="shrink-0 pt-3 pb-1.5"
      >
        <wui-tabs
          :model-value="selectedFolderId"
          scrollable
          class="min-w-0 overflow-hidden"
          @update:model-value="onFolderChange"
        >
          <wui-tabs-item :value="ALL_CHATS_FOLDER_ID">
            {{ $t('common.all') }}
          </wui-tabs-item>
          <wui-tabs-item
            v-for="folder in folders"
            :key="folder.id"
            :value="folder.id"
            :title="folder.name"
          >
            {{ formatFolderTabName(folder.name) }}
          </wui-tabs-item>
        </wui-tabs>
      </div>

      <div class="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto p-1">
        <template
          v-for="(room, index) in displayRooms"
          :key="room.pServedUser"
        >
          <hr
            v-if="index === dividerBeforeIndex"
            class="my-1 border-t-bor-nav"
            data-test="share-contact-pinned-divider"
          >
          <button
            type="button"
            class="flex w-full items-center gap-3 rounded-8 py-1.5 text-left"
            :data-test="`share-contact-room-${room.pServedUser}`"
            @click="toggleRoom(room.pServedUser)"
          >
            <contact-avatar
              :room="room"
              :is-room="true"
              :size="40"
              class="shrink-0"
            />
            <span
              class="min-w-0 grow truncate text-body-m-15-22 text-fg-base-fg3"
              :title="room.name"
            >
              {{ room.name }}
            </span>
            <wui-checkbox
              :model-value="selectedPServeds.includes(room.pServedUser)"
              class="pointer-events-none shrink-0"
              small
              readonly
            />
          </button>
        </template>

        <p
          v-if="!displayRooms.length"
          class="px-3 py-2 text-fg-base-fg10"
        >
          {{ $t('chat.noChatsFound') }}
        </p>
      </div>

      <div class="relative shrink-0">
        <emoji-picker
          v-if="showEmojiList"
          class="absolute bottom-full left-0 z-20 mb-1"
          close-on-click-outside
          data-test="share-contact-emoji-picker"
          @append="appendEmoji"
          @escape="closeEmojiList"
          @close="closeEmojiList"
        />

        <div class="flex items-end gap-2 pt-2 pb-4">
          <div class="flex min-w-0 flex-1 items-end gap-1.5 rounded-10 bg-surf-neut-soft-def p-2">
            <textarea
              ref="commentInputRef"
              v-model="messageText"
              rows="1"
              class="max-h-24 min-h-6 min-w-0 flex-1 resize-none bg-transparent text-body-r-16-24 text-fg-base-fg3 outline-none placeholder:text-fg-base-fg17"
              :placeholder="$t('mainAside.shareContactToChatMessagePlaceholder')"
              data-test="share-contact-comment"
              @keydown.enter.exact.prevent="send"
            />
            <button
              type="button"
              class="flex h-6 w-6 shrink-0 items-center justify-center"
              data-test="share-contact-emoji"
              @mousedown.prevent
              @click="toggleEmojiList"
            >
              <wui-icon
                name="faceM"
                class="h-6! w-6! text-fg-base-fg22!"
              />
              <span class="sr-only">{{ $t('chat.openEmoji') }}</span>
            </button>
          </div>

          <wui-btn
            class="mb-0.5 shrink-0"
            icon
            :size="40"
            variant="brand"
            state="filled"
            prepend-icon="paperPlaneM"
            :disabled="!selectedPServeds.length || isSending"
            data-test="send-contact-to-chat-btn"
            @click="send"
          >
            <span class="sr-only">{{ $t('chat.sendForward') }}</span>
          </wui-btn>
        </div>
      </div>
    </div>
  </wui-modal>
</template>

<script setup lang="ts">
import {
  closeDialogKey,
  safeInject,
  useRenderNotifier,
  WuiBtn,
  WuiCheckbox,
  WuiIcon,
  WuiInput,
  WuiModal,
  WuiTabs,
  WuiTabsItem,
} from '@wui/common-library'
import type { PServed } from '@wui/im'
import { storeToRefs } from 'pinia'
import { computed, defineAsyncComponent, nextTick, ref, watch } from 'vue'
import { useRoute } from 'vue-router'


import { useFilesUpload } from '@/features/chat-files-upload'

import { ALL_CHATS_FOLDER_ID, filterRoomsByFolder, useChatFolders } from '@/entities/chat-folder'
import { useStoreContacts } from '@/entities/contact'
import { ContactAvatar } from '@/entities/contact-avatar'
import { useStoreRooms } from '@/entities/room'
import { useRoom } from '@/entities/use-room'

import { useLocalization } from '@/shared/composables/localization'
import { useEmoji } from '@/shared/composables/use-emoji'
import type { Contact } from '@/shared/types/api'
import { convertUploadingFiles } from '@/shared/utils/convert-uploading-files'
import { createPrivateRoom } from '@/shared/utils/im'
import { contactToVCardFile } from '@/shared/utils/vcard'

const EmojiPicker = defineAsyncComponent(async () => ((await import('@/features/emoji-picker')).EmojiPicker))

const FOLDER_TAB_NAME_MAX_LENGTH = 18

const props = defineProps<{
  contact: Contact
}>()

const closeModal = safeInject(closeDialogKey)
const { tc, t } = useLocalization()
const { notifyError } = useRenderNotifier()
const route = useRoute()
const roomApi = useRoom()
const storeRooms = useStoreRooms()
const storeContacts = useStoreContacts()
const { roomsWithOtherContactsListItems } = storeToRefs(storeRooms)
const { folders } = useChatFolders()
const { showEmojiList, toggleEmojiList, closeEmojiList } = useEmoji()

const search = ref('')
const selectedFolderId = ref(ALL_CHATS_FOLDER_ID)
const selectedPServeds = ref<PServed[]>([])
const messageText = ref('')
const isSending = ref(false)
const commentInputRef = ref<HTMLTextAreaElement | null>(null)

const formatFolderTabName = (name: string) => (
  name.length > FOLDER_TAB_NAME_MAX_LENGTH
    ? `${name.slice(0, FOLDER_TAB_NAME_MAX_LENGTH)}…`
    : name
)

const modalTitle = computed(() => {
  if (!selectedPServeds.value.length) {
    return t('chat.whereToForward')
  }

  return tc('chat.selectedChatsCount', selectedPServeds.value.length, {
    n: selectedPServeds.value.length,
  })
})

const folderRooms = computed(() => filterRoomsByFolder(
  roomsWithOtherContactsListItems.value,
  selectedFolderId.value,
  folders.value,
))

const matchingRooms = computed(() => {
  const query = search.value.trim().toLowerCase()
  if (!query) {
    return folderRooms.value
  }

  return folderRooms.value.filter((room) => room.name.toLowerCase().includes(query))
})

const pinnedRooms = computed(() => matchingRooms.value.filter((room) => room.isPinned))
const unpinnedRooms = computed(() => matchingRooms.value.filter((room) => !room.isPinned))
const displayRooms = computed(() => [...pinnedRooms.value, ...unpinnedRooms.value])
const dividerBeforeIndex = computed(() => {
  if (!pinnedRooms.value.length || !unpinnedRooms.value.length) {
    return -1
  }

  return pinnedRooms.value.length
})

const onFolderChange = (value: string | number) => {
  selectedFolderId.value = String(value)
}

const toggleRoom = (pServedUser: PServed) => {
  if (selectedPServeds.value.includes(pServedUser)) {
    selectedPServeds.value = selectedPServeds.value.filter((item) => item !== pServedUser)
    return
  }

  selectedPServeds.value = [...selectedPServeds.value, pServedUser]
}

const appendEmoji = async (emoji: string) => {
  const input = commentInputRef.value
  if (!input) {
    messageText.value += emoji
    return
  }

  const start = input.selectionStart ?? messageText.value.length
  const end = input.selectionEnd ?? messageText.value.length
  messageText.value = `${messageText.value.slice(0, start)}${emoji}${messageText.value.slice(end)}`
  await nextTick()
  const caret = start + emoji.length
  input.focus()
  input.setSelectionRange(caret, caret)
}

const fileListFromFile = (file: File): FileList => {
  const dt = new DataTransfer()
  dt.items.add(file)
  return dt.files
}

const closeDialog = () => {
  closeModal(undefined)
}

const resolveRoom = (pServed: PServed) => {
  const room = storeRooms.findByPServed(pServed)
  if (room) {
    return room
  }

  const contact = storeContacts.findByPServed(pServed)
  if (!contact) {
    return undefined
  }

  return createPrivateRoom(pServed, contact)
}

const send = async () => {
  if (!selectedPServeds.value.length || isSending.value) {
    return
  }

  isSending.value = true
  closeEmojiList()

  try {
    const vcardFile = contactToVCardFile(props.contact)
    const chatFiles = convertUploadingFiles(fileListFromFile(vcardFile), 'share-contact')
    await useFilesUpload().uploadFiles(chatFiles)
    const loadedFiles = chatFiles.filter((file) => file.isLoaded.value && !file.isError.value)
    if (!loadedFiles.length) {
      notifyError('Failed to upload contact')
      return
    }

    const text = messageText.value.trim()
    for (const pServed of selectedPServeds.value) {
      const room = resolveRoom(pServed)
      if (!room) {
        continue
      }

      await roomApi.send(text, room, loadedFiles).catch((error: Error) => {
        notifyError(error.message)
      })
    }

    closeDialog()
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error'
    notifyError(message)
  } finally {
    isSending.value = false
  }
}

watch(() => route.path, () => {
  closeDialog()
})

watch(folders, (nextFolders) => {
  if (
    selectedFolderId.value !== ALL_CHATS_FOLDER_ID
    && !nextFolders.some((folder) => folder.id === selectedFolderId.value)
  ) {
    selectedFolderId.value = ALL_CHATS_FOLDER_ID
  }
})
</script>
